import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink, Quote } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { CASE_STUDIES, getCaseStudyBySlug } from "@/data/caseStudies";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const cs = getCaseStudyBySlug(slug);

  if (!cs) return <Navigate to="/case-studies" replace />;

  const accentText = cs.accent === "green" ? "text-neon-green" : "text-neon-cyan";
  const accentBorder =
    cs.accent === "green" ? "border-neon-green/40" : "border-neon-cyan/40";
  const accentGlow =
    cs.accent === "green" ? "text-glow-green" : "text-glow-cyan";

  // Find next case study for footer nav
  const idx = CASE_STUDIES.findIndex((x) => x.slug === cs.slug);
  const next = CASE_STUDIES[(idx + 1) % CASE_STUDIES.length];

  return (
    <>
      <SEO
        title={`${cs.client} | Case Study`}
        description={cs.summary.slice(0, 180)}
        path={`/case-studies/${cs.slug}`}
        type="article"
      />

      {/* Hero */}
      <section
        data-testid={`case-study-hero-${cs.slug}`}
        className="relative border-b border-white/10 bg-obsidian overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <Link
            to="/case-studies"
            data-testid="back-to-case-studies"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white/50 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> back to case studies
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className={`chip ${accentText}`}>// case_study</span>
            <span className="chip text-white/40">// {cs.industry}</span>
            <span className="chip text-white/40">// {cs.duration}</span>
          </div>

          <h1 className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-7xl text-white tracking-tighter leading-[0.95] max-w-5xl">
            {cs.client}
          </h1>
          <p className="mt-6 max-w-3xl text-white/70 text-base lg:text-lg leading-relaxed">
            {cs.headline}
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            {cs.services.map((s) => (
              <span
                key={s}
                className="text-[11px] font-mono text-white/70 border border-white/15 px-3 py-1.5"
              >
                {s}
              </span>
            ))}
          </div>

          {cs.url && cs.url !== "#" && (
            <a
              href={cs.url}
              target="_blank"
              rel="noreferrer"
              data-testid="case-study-external-link"
              className={`mt-8 inline-flex items-center gap-2 text-sm font-mono ${accentText} hover:${accentGlow} transition-all`}
            >
              Visit {cs.client} <ExternalLink size={14} />
            </a>
          )}
        </div>
      </section>

      {/* Outcomes */}
      <section className="relative border-b border-white/10 bg-carbon">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="chip text-white/50">&gt; /headline_outcomes</div>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {cs.outcomes.map((o, i) => (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-obsidian p-6 lg:p-8"
                data-testid={`outcome-${i}`}
              >
                <div
                  className={`font-display font-black text-3xl lg:text-4xl ${accentText}`}
                >
                  {o.value}
                </div>
                <div className="mt-2 text-xs font-mono text-white/55 leading-tight uppercase tracking-wider">
                  {o.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Summary + Pull Quote */}
      <section className="relative border-b border-white/10 bg-obsidian">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="chip text-white/50">&gt; /the_engagement</div>
            <p className="mt-5 text-white/80 text-lg leading-relaxed font-display">
              {cs.summary}
            </p>
          </div>
          <figure
            className={`lg:col-span-5 relative border ${accentBorder} bg-carbon p-8 lg:p-10`}
            data-testid="pull-quote"
          >
            <Quote
              className={`absolute top-5 right-5 opacity-20 ${accentText}`}
              size={36}
            />
            <blockquote className="text-base lg:text-lg leading-relaxed text-white font-display">
              &ldquo;{cs.pullQuote}&rdquo;
            </blockquote>
            <figcaption className="mt-6 pt-4 border-t border-white/10 text-xs font-mono text-white/50 uppercase tracking-wider">
              — {cs.pullQuoteRole}
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Problem / Approach */}
      <section className="relative border-b border-white/10 bg-carbon">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div data-testid="problem-block">
            <div className="chip text-neon-cyan">&gt; /the_problem</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl text-white tracking-tight leading-tight">
              What they were up against.
            </h2>
            <ul className="mt-8 space-y-5">
              {cs.problem.map((p, i) => (
                <li
                  key={i}
                  className="flex gap-4 text-white/75 text-sm leading-relaxed"
                >
                  <span className="font-mono text-neon-cyan/70 text-xs pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="approach-block">
            <div className="chip text-neon-green">&gt; /our_approach</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl text-white tracking-tight leading-tight">
              How we ran the program.
            </h2>
            <ul className="mt-8 space-y-5">
              {cs.approach.map((a, i) => (
                <li
                  key={i}
                  className="flex gap-4 text-white/75 text-sm leading-relaxed"
                >
                  <span className="font-mono text-neon-green/70 text-xs pt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Deliverables + Tech */}
      <section className="relative border-b border-white/10 bg-obsidian">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5" data-testid="deliverables-block">
            <div className="chip text-white/50">&gt; /deliverables</div>
            <h3 className="mt-3 font-display font-black text-2xl lg:text-3xl text-white tracking-tight">
              What we shipped.
            </h3>
            <ul className="mt-6 space-y-3">
              {cs.deliverables.map((d) => (
                <li
                  key={d}
                  className="flex items-start gap-3 text-white/75 text-sm leading-relaxed"
                >
                  <span className={`mt-2 h-1.5 w-1.5 ${cs.accent === "green" ? "bg-neon-green" : "bg-neon-cyan"}`} />
                  {d}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7" data-testid="tech-block">
            <div className="chip text-white/50">&gt; /tech_used</div>
            <h3 className="mt-3 font-display font-black text-2xl lg:text-3xl text-white tracking-tight">
              The stack.
            </h3>
            <div className="mt-6 flex flex-wrap gap-2">
              {cs.techUsed.map((t) => (
                <span
                  key={t}
                  className="text-xs font-mono text-white/80 bg-carbon border border-white/10 hover:border-white/30 px-3 py-2 transition-colors"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Next case study */}
      <section className="relative border-b border-white/10 bg-carbon">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
          <Link
            to={`/case-studies/${next.slug}`}
            data-testid="next-case-study"
            className="group flex items-center justify-between gap-6 hover:bg-obsidian -mx-5 px-5 lg:-mx-8 lg:px-8 py-6 transition-colors"
          >
            <div>
              <div className="chip text-white/50">// next case study</div>
              <div className="mt-2 font-display font-black text-2xl lg:text-3xl text-white group-hover:text-neon-green transition-colors">
                {next.client}
              </div>
            </div>
            <ArrowRight
              size={28}
              className="text-white/40 group-hover:text-neon-green group-hover:translate-x-1 transition-all"
            />
          </Link>
        </div>
      </section>

      <CTASection
        title="Want a program like this for your team?"
        subtitle="Tell us what you're modernizing, building, or scaling. We'll reply with a pragmatic path forward, not a sales pitch."
        ctaLabel="Start a conversation"
      />
    </>
  );
}
